import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, companyApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { QuestionStatusBadge } from '../components/StatusBadge.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { DateField } from '../components/DateField.tsx';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { useAbnVerification } from '../lib/useAbnVerification.ts';
import { useAutoFillField } from '../lib/autoFillField.ts';
import { AbnStatusIndicator } from '../components/AbnStatus.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Markdown } from '../components/Markdown.tsx';
import { Modal } from '../components/Modal.tsx';
import { Tabs } from '../components/Tabs.tsx';
import type {
  Company,
  CompanyDocument,
  Json,
  Question,
  QuestionCategory,
  Questionnaire,
  Submission,
  SubmissionComment,
} from '../lib/types.ts';
import {
  activeAdoptionTargets,
  computeProgress,
  questionCompletion,
  type CompletionReason,
} from '../lib/visibility.ts';
import { blockerHint, stepBlockers } from '../lib/stepBlockers.ts';
import { isStepLocked } from '../lib/stepLock.ts';
import {
  applyLocalAnswers,
  buildSteps,
  isPersistedSubmission,
  stepIndexForQuestion,
  type LocalAnswer,
  type Step,
} from '../lib/wizard.ts';
import { missingCompanyInfoFields, REQUIRED_COMPANY_INFO_FIELDS } from '@l2u/shared/progress';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import { expiryState, fromDateInput, toDateInput, type ExpiryState } from '../lib/expiry.ts';
import { fileKindLabel, formatFileSize } from '../lib/fileDisplay.ts';
import { summarizeAnswer, type AnswerPart } from '../lib/answerSummary.ts';
import type { SubmissionFile } from '../lib/types.ts';
import { companyInfoLabel } from '../lib/companyInfo.ts';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import { postSubmitLocked } from '../lib/outstandingActions.ts';
import {
  questionnaireVendorStatus,
  type QuestionnaireVendorStatus,
} from '../lib/questionnaireStatus.ts';
import { needsVendorAction, questionnaireSubmitted } from '../lib/vendorActions.ts';
import { questionDisplayStatus } from '../lib/questionDisplayStatus.ts';
import { reviewTurn, VENDOR_REVIEW_LABEL, type ReviewTurn } from '../lib/reviewState.ts';
import { groupByCategoryOrdered } from '../lib/postSubmitGrouping.ts';
import { QuestionnaireTypePicker } from './QuestionnaireTypePicker.tsx';
import { CommentThread } from '../components/CommentThread.tsx';
import { ReplyComposer, type ReplyPayload } from '../components/ReplyComposer.tsx';
import { DocumentTable } from '../components/DocumentTable.tsx';
import {
  hasExpiredLiveFile,
  showDocumentTable,
  type DocVersionFile,
} from '../lib/documentVersions.ts';
import {
  COMPANY_INFO_FORM_FIELDS,
  buildCompanyInfoPayload,
  companyInfoFormMissing,
  companyInfoStepVisible,
  toCompanyInfoFormValues,
  type CompanyInfoFormValues,
} from './companyInfoForm.ts';

// API prefix the inner components should hit when they save submissions /
// upload files. Companies use /api/me, drivers use /api/driver/me. We pass
// both the prefix and the relevant React Query keys through context so the
// deeply-nested QuestionCard children don't each need an explicit prop.
//
// setLocalAnswer / clearLocalAnswer let a QuestionCard report the answer the
// user just entered up to the wizard, so the step gate reflects it immediately
// instead of only after the save round-trips back from the server.
type WizardContextValue = {
  apiPrefix: string;
  meQueryKey: readonly unknown[];
  submissionsQueryKey: readonly unknown[];
  setLocalAnswer: (questionId: string, answer: Json | null, comment: string | null) => void;
  clearLocalAnswer: (questionId: string) => void;
};
const WizardContext = createContext<WizardContextValue>({
  apiPrefix: '/api/me',
  meQueryKey: ['me'],
  submissionsQueryKey: ['me', 'submissions'],
  setLocalAnswer: () => {},
  clearLocalAnswer: () => {},
});

export function useWizardCtx(): WizardContextValue {
  return useContext(WizardContext);
}

type QuestionnaireWizardProps = {
  apiPrefix?: string;
  meQueryKey?: readonly unknown[];
  submitButtonLabel?: string;
  // Optional "Exit and save for later" target. When set, renders a centred
  // link below the form footer. Progress is saved on every answer change, so
  // exiting just leaves the page.
  exitTo?: string;
  // Optional extra completion gate (e.g. missing company info fields) that
  // disables the Submit button on the last step and renders a custom hint.
  extraGate?: { count: number; render: () => React.ReactNode } | null;
  // When true, prepends a "Company information" step that captures the
  // company's required profile fields before the questionnaire categories.
  includeCompanyInfoStep?: boolean;
  // When true (vendor flow), appends a final read-only "Review" step with a
  // consent checkbox and a "Submit Application" button instead of submitting
  // directly from the last category step.
  includeReviewStep?: boolean;
};

export function Questionnaire() {
  return (
    <QuestionnaireWizard
      apiPrefix="/api/me"
      meQueryKey={['me']}
      includeCompanyInfoStep
      includeReviewStep
    />
  );
}

export function QuestionnaireWizard({
  apiPrefix = '/api/me',
  meQueryKey = ['me'],
  submitButtonLabel = 'Submit for review',
  exitTo,
  extraGate = null,
  includeCompanyInfoStep = false,
  includeReviewStep = false,
}: QuestionnaireWizardProps) {
  const qc = useQueryClient();
  const submissionsKey = [...meQueryKey, 'submissions'] as const;

  const meQuery = useQuery({
    queryKey: meQueryKey,
    queryFn: () => companyApi.get<{ company: Company }>(apiPrefix),
    enabled: includeCompanyInfoStep,
  });
  const questionsQuery = useQuery({
    queryKey: [`${apiPrefix}/questions`],
    queryFn: () => companyApi.get<{ questions: Question[] }>(`${apiPrefix}/questions`),
  });
  const submissionsQuery = useQuery({
    queryKey: submissionsKey,
    queryFn: () => companyApi.get<{ submissions: Submission[] }>(`${apiPrefix}/submissions`),
  });
  const documentsQuery = useQuery({
    queryKey: [`${apiPrefix}/documents`],
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>(`${apiPrefix}/documents`),
  });
  // Resolve which questionnaire the vendor is viewing *before* fetching
  // categories, so the category list can be scoped to that one questionnaire
  // (its own admin-configured order) rather than the merge across all applicable
  // questionnaires. (Declared here — ahead of categoriesQuery — for that reason.)
  const questionnairesQuery = useQuery({
    queryKey: [`${apiPrefix}/questionnaires`],
    queryFn: () =>
      companyApi.get<{ questionnaires: Questionnaire[] }>(`${apiPrefix}/questionnaires`),
  });
  const applicableQuestionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTypeId = searchParams.get('type');
  const effectiveTypeId: string | null = useMemo(() => {
    if (selectedTypeId) return selectedTypeId;
    if (applicableQuestionnaires.length === 1) return applicableQuestionnaires[0]!.id;
    return null;
  }, [selectedTypeId, applicableQuestionnaires]);
  const categoriesQuery = useQuery({
    // Keyed on effectiveTypeId so switching questionnaire refetches the scoped
    // order. When null (multiple questionnaires apply and none selected → the
    // type picker is showing, which does not render categories), fetch unscoped.
    queryKey: [`${apiPrefix}/question-categories`, effectiveTypeId],
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>(
        `${apiPrefix}/question-categories${
          effectiveTypeId ? `?questionnaire_id=${effectiveTypeId}` : ''
        }`,
      ),
  });

  const submitMutation = useMutation({
    mutationFn: (body?: { accept_terms?: boolean; questionnaire_id?: string | null }) =>
      companyApi.post(`${apiPrefix}/submit`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meQueryKey });
      void qc.invalidateQueries({ queryKey: submissionsKey });
    },
  });

  const allQuestions = questionsQuery.data?.questions ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const documents = documentsQuery.data?.documents ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const company = meQuery.data?.company;

  function selectType(id: string) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('type', id);
        return p;
      },
      { replace: false },
    );
  }
  function clearType() {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('type');
        return p;
      },
      { replace: false },
    );
  }

  // Scope questions to the chosen questionnaire.
  const questions = useMemo(() => {
    if (!effectiveTypeId) return allQuestions;
    return allQuestions.filter((q) => q.questionnaire_id === effectiveTypeId);
  }, [allQuestions, effectiveTypeId]);

  // Per-questionnaire vendor-action status for the picker cards. Uses the full
  // allQuestions/submissions sets (loaded regardless of selection) so each
  // questionnaire's status is known before the vendor opens it.
  const statusById = useMemo(() => {
    const map = new Map<string, QuestionnaireVendorStatus>();
    const subByQAll = new Map(submissions.map((s) => [s.question_id, s]));
    for (const questionnaire of applicableQuestionnaires) {
      const scoped = allQuestions.filter((q) => q.questionnaire_id === questionnaire.id);
      // Finished = all visible required questions complete (same measure as the
      // wizard progress bar). `submitted` keys off submitted_at (the same gate
      // as the post-submit view) so a finished-but-unsent questionnaire reads
      // "pending submission", a submitted one under review reads "under review",
      // and only a published all-approved verdict reads "compliant".
      const fullyAnswered = computeProgress(scoped, submissions).progress === 100;
      const submitted = questionnaireSubmitted(scoped, subByQAll);
      map.set(
        questionnaire.id,
        questionnaireVendorStatus(scoped, submissions, { fullyAnswered, submitted }),
      );
    }
    return map;
  }, [applicableQuestionnaires, allQuestions, submissions]);

  // Once the questionnaire is submitted (review/compliant), answers already on
  // file are read-only — only questions that were still unanswered when the
  // wizard loaded (e.g. added by the admin after submission) stay editable.
  // Answered-ness is snapshotted at first load so a new question doesn't lock
  // itself the moment its first answer auto-saves back from the server.
  const answeredAtLoadRef = useRef<Set<string> | null>(null);
  if (answeredAtLoadRef.current === null && submissionsQuery.data) {
    answeredAtLoadRef.current = new Set(
      submissions
        .filter(
          (s) =>
            s.answer !== null || s.submission_files.length > 0 || s.submission_adoptions.length > 0,
        )
        .map((s) => s.question_id),
    );
  }
  // Whether THIS questionnaire is submitted — which drives both the post-submit
  // read-only view and the answer lock — is computed below, after `breakdown`,
  // because it depends on this questionnaire's own progress, not just the
  // company-wide status. See `questionnaireSubmitted` and `submittedLock` below.

  // Answers entered this session, overlaid on the server-fetched submissions so
  // the "Continue" gate reflects what the company just typed/selected the moment
  // they do it — not only after the PUT round-trips back. This is what stops a
  // freshly-answered question from being listed as "still required".
  const [localAnswers, setLocalAnswers] = useState<Map<string, LocalAnswer>>(() => new Map());
  const setLocalAnswer = useCallback(
    (questionId: string, answer: Json | null, comment: string | null) => {
      setLocalAnswers((prev) => {
        const next = new Map(prev);
        next.set(questionId, { answer, comment });
        return next;
      });
    },
    [],
  );
  const clearLocalAnswer = useCallback((questionId: string) => {
    setLocalAnswers((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Map(prev);
      next.delete(questionId);
      return next;
    });
  }, []);

  const effectiveSubmissions = useMemo(
    () => applyLocalAnswers(submissions, localAnswers),
    [submissions, localAnswers],
  );

  const docMap = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const subByQ = useMemo(() => {
    const m = new Map<string, Submission>();
    for (const s of effectiveSubmissions) m.set(s.question_id, s);
    return m;
  }, [effectiveSubmissions]);

  const questionById = useMemo(() => new Map(questions.map((x) => [x.id, x])), [questions]);

  const breakdown = useMemo(
    () => computeProgress(questions, effectiveSubmissions),
    [questions, effectiveSubmissions],
  );

  // A questionnaire shows its post-submit (read-only) category view — and locks
  // answers already on file — only once IT has actually been submitted, keyed
  // off each answer's persisted `submitted_at` stamp (set by POST /me/submit),
  // never the company-wide status. So a vendor who is 'review'/'changes'/
  // 'compliant' from an earlier questionnaire keeps the fillable wizard for a
  // brand-new one: filling in answers is not submitting — only the Review step's
  // Submit button is.
  const selectedQuestionnaireSubmitted = questionnaireSubmitted(questions, subByQ);
  // Locks individual answers once this questionnaire is submitted ('changes'
  // questions flipped to 'rejected' stay editable — handled in QuestionCard).
  const submittedLock = selectedQuestionnaireSubmitted;

  const extraGateCount = extraGate?.count ?? 0;
  const companyInfoMissingCount = company ? missingCompanyInfoFields(company).length : 0;

  // Show the "Vendor information" step until the vendor submits for the first
  // time; afterwards it is omitted so later questionnaires don't re-prompt for
  // company info already on file. Reappears if a required field is still
  // missing — see companyInfoStepVisible.
  const showCompanyInfoStep = companyInfoStepVisible(
    includeCompanyInfoStep,
    company?.has_submitted_before ?? false,
    companyInfoMissingCount,
  );

  const steps = useMemo<Step[]>(
    () =>
      buildSteps(
        questions,
        categories,
        breakdown.visibleIds,
        showCompanyInfoStep,
        includeReviewStep,
      ),
    [questions, categories, breakdown.visibleIds, showCompanyInfoStep, includeReviewStep],
  );

  const [stepIndex, setStepIndex] = useState(0);

  // Deep-link support: ?question=<id> (Documents page "Re-upload") opens the
  // step containing that question, then strips the param so refreshes and
  // step clicks behave normally. Unresolvable ids fall through to step 0.
  // searchParams / setSearchParams are declared above (shared with ?type= picker).
  const targetQuestionId = searchParams.get('question');
  useEffect(() => {
    if (!targetQuestionId || steps.length === 0) return;
    const idx = stepIndexForQuestion(steps, targetQuestionId);
    if (idx >= 0) setStepIndex(idx);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('question');
        return p;
      },
      { replace: true },
    );
  }, [targetQuestionId, steps, setSearchParams]);

  // After submission, land the vendor on the first step that still has an
  // unanswered question (newly added by the admin) instead of step 1. The
  // ?question= deep-link takes precedence; jump at most once per mount.
  const autoJumpedRef = useRef(false);
  useEffect(() => {
    if (autoJumpedRef.current || steps.length === 0) return;
    if (targetQuestionId) {
      autoJumpedRef.current = true; // deep-link owns the initial navigation
      return;
    }
    const answered = answeredAtLoadRef.current;
    if (!submittedLock || !answered) return;
    autoJumpedRef.current = true;
    const idx = steps.findIndex(
      (s) => s.kind === 'questions' && s.questions.some((q) => !answered.has(q.id)),
    );
    if (idx >= 0) setStepIndex(idx);
  }, [steps, targetQuestionId, submittedLock]);

  // The required question to scroll to / flash when the user tries to advance
  // before completing the step.
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Task 7: submit-confirmation summary modal state. Must be declared with the
  // other hooks (before any early return below) — Rules of Hooks require every
  // hook to run on every render in the same order.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Vendor review-step accuracy + legal consent. A single checkbox now covers
  // both the accuracy confirmation and (when configured) the Terms/Privacy
  // agreement. Client-side gate only.
  const [consentChecked, setConsentChecked] = useState(false);
  const { settings } = usePortalSettings();
  const hasTermsContent = !!(settings?.termsContent || settings?.privacyContent);

  // Clamp the active step whenever the visible step count changes (e.g. an
  // answer hides a follow-up category) so we never end up past the last step.
  useEffect(() => {
    if (steps.length === 0) return;
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length, stepIndex]);

  // Drop the highlight when the active step changes.
  useEffect(() => {
    setHighlightId(null);
  }, [stepIndex]);

  function stepCompletion(step: Step): { required: number; complete: number } {
    if (step.kind === 'company-info') {
      const total = REQUIRED_COMPANY_INFO_FIELDS.length;
      return { required: total, complete: total - companyInfoMissingCount };
    }
    if (step.kind === 'review') {
      return { required: 1, complete: consentChecked ? 1 : 0 };
    }
    let required = 0;
    let complete = 0;
    for (const q of step.questions) {
      if (!q.required) continue;
      required += 1;
      const reason = breakdown.completionByQuestion.get(q.id);
      if (reason === null) complete += 1;
    }
    return { required, complete };
  }

  function stepLabel(step: Step): string {
    if (step.kind === 'company-info') return 'Vendor information';
    if (step.kind === 'review') return 'Review';
    return step.category.name;
  }

  if (
    questionsQuery.isLoading ||
    submissionsQuery.isLoading ||
    documentsQuery.isLoading ||
    categoriesQuery.isLoading ||
    questionnairesQuery.isLoading ||
    (includeCompanyInfoStep && meQuery.isLoading)
  ) {
    return <CenteredSpinner />;
  }

  // Task 11: show the type picker when multiple questionnaires apply and none
  // is selected yet.
  const showTypePicker = applicableQuestionnaires.length > 1 && !effectiveTypeId;

  if (showTypePicker) {
    return (
      <QuestionnaireTypePicker
        questionnaires={applicableQuestionnaires}
        statusById={statusById}
        onSelect={selectType}
      />
    );
  }

  if (steps.length === 0) {
    return (
      <EmptyState
        title="No questions yet"
        description="Check back once your administrator has set up the questionnaire."
      />
    );
  }

  // Task 12: after submission show the admin-style category view with pending badges.
  if (selectedQuestionnaireSubmitted) {
    return (
      <PostSubmitCategoryView
        key={effectiveTypeId ?? ''}
        company={company}
        questions={questions}
        submissions={effectiveSubmissions}
        categories={categories}
        documents={documents}
        visibleIds={breakdown.visibleIds}
        completionByQuestion={breakdown.completionByQuestion}
        subByQ={subByQ}
        docMap={docMap}
        questionById={questionById}
        applicableQuestionnaires={applicableQuestionnaires}
        onClearType={clearType}
        apiPrefix={apiPrefix}
        meQueryKey={meQueryKey}
        submissionsQueryKey={submissionsKey}
        defaultActionTab={searchParams.get('action') === '1'}
      />
    );
  }

  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[safeIndex]!;
  const isLastStep = safeIndex === steps.length - 1;
  const currentComplete = stepCompletion(currentStep);
  const canAdvance = currentComplete.complete >= currentComplete.required;

  // Required questions in the current step that are still incomplete, so we can
  // tell the user *what* to finish rather than just greying out the button.
  const blockers =
    currentStep.kind === 'questions'
      ? stepBlockers(currentStep.questions, breakdown.completionByQuestion)
      : [];
  const submitReady =
    canAdvance &&
    breakdown.progress >= 100 &&
    companyInfoMissingCount === 0 &&
    extraGateCount === 0;

  // Everything answered/complete, independent of the review-step consent box.
  // Used by the Review step's "Submit Application" gate (consent is separate).
  const contentComplete =
    breakdown.progress >= 100 && companyInfoMissingCount === 0 && extraGateCount === 0;

  function revealBlocker(id: string) {
    setHighlightId(id);
    if (typeof document !== 'undefined') {
      document.getElementById(`q-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
  function revealFirstBlocker() {
    if (blockers[0]) revealBlocker(blockers[0].question.id);
  }
  // Index of the first step that still has something outstanding — used to send
  // the user back to it when they try to submit before finishing.
  function firstIncompleteStepIndex(): number {
    for (let i = 0; i < steps.length; i += 1) {
      const s = steps[i]!;
      if (s.kind === 'company-info') {
        if (companyInfoMissingCount > 0) return i;
      } else if (
        s.kind === 'questions' &&
        stepBlockers(s.questions, breakdown.completionByQuestion).length > 0
      ) {
        return i;
      }
    }
    return -1;
  }

  function handleSubmitClick() {
    if (submitReady) {
      // Intercept: show the confirm modal instead of submitting immediately.
      setShowConfirmModal(true);
      return;
    }
    if (!canAdvance) {
      revealFirstBlocker();
      return;
    }
    const idx = firstIncompleteStepIndex();
    if (idx >= 0) setStepIndex(idx);
  }

  function handleConfirmSubmit() {
    setShowConfirmModal(false);
    submitMutation.mutate({ questionnaire_id: effectiveTypeId });
  }

  function handleSubmitApplication() {
    if (!contentComplete) {
      const idx = firstIncompleteStepIndex();
      if (idx >= 0) setStepIndex(idx);
      return;
    }
    if (!consentChecked) return;
    submitMutation.mutate({
      ...(hasTermsContent ? { accept_terms: true } : {}),
      questionnaire_id: effectiveTypeId,
    });
  }

  return (
    <WizardContext.Provider
      value={{
        apiPrefix,
        meQueryKey,
        submissionsQueryKey: submissionsKey,
        setLocalAnswer,
        clearLocalAnswer,
      }}
    >
      <div className="space-y-5">
        {/* Task 11: back-link to the picker when multiple questionnaires exist */}
        {applicableQuestionnaires.length > 1 && (
          <button
            type="button"
            onClick={clearType}
            className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
          >
            ← All questionnaires
          </button>
        )}

        {/* Reaching the wizard means THIS questionnaire is not yet submitted
            (a submitted one renders PostSubmitCategoryView above). So the only
            valid trigger for this banner is a submit the vendor just made in
            this session — never the company-wide status, which is shared across
            questionnaires and would mislabel a brand-new one as "submitted". */}
        {submitMutation.isSuccess && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Submitted for review.
          </div>
        )}

        <Stepper
          steps={steps}
          activeIndex={safeIndex}
          completionFor={stepCompletion}
          labelFor={stepLabel}
          lockedFrom={firstIncompleteStepIndex()}
          onSelect={(i) => setStepIndex(i)}
        />

        {/* Mobile replacement for the stepper: step counter, active step name,
            and an overall progress bar. The circles render from sm up. */}
        <div className="sm:hidden">
          <div className="text-xs text-slate-500">
            Step {safeIndex + 1} of {steps.length}
          </div>
          <div className="mt-0.5 text-base font-semibold" style={{ color: 'var(--brand-ink)' }}>
            {stepLabel(currentStep)}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1">
              <ProgressBar value={breakdown.progress} />
            </div>
            <span className="text-xs font-medium text-slate-600">{breakdown.progress}%</span>
          </div>
        </div>

        <div className="hidden items-center justify-between text-xs text-slate-500 sm:flex">
          <span>
            {breakdown.completedRequired}/{breakdown.totalRequired} required complete
          </span>
          <span className="font-medium">{breakdown.progress}%</span>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
              Step {safeIndex + 1} of {steps.length}
            </div>
            <h2
              className="mt-1 text-2xl font-bold tracking-tight"
              style={{ color: 'var(--brand-ink)' }}
            >
              {stepLabel(currentStep)}
            </h2>
          </div>
          {currentStep.kind === 'company-info' && company ? (
            <CompanyInfoStep
              company={company}
              isLastStep={isLastStep}
              onSaved={() => {
                const next = safeIndex + 1;
                // When company info is the only/last step (no questionnaire
                // questions apply), there is nothing to advance to — submit
                // instead of bouncing back to the same step.
                if (next < steps.length) setStepIndex(next);
                else submitMutation.mutate(undefined);
              }}
              meQueryKey={meQueryKey}
              apiPrefix={apiPrefix}
            />
          ) : currentStep.kind === 'questions' ? (
            currentStep.questions.map((q) => (
              <div
                key={q.id}
                id={`q-${q.id}`}
                className={`scroll-mt-24 ${
                  highlightId === q.id ? 'rounded-2xl ring-2 ring-amber-400 ring-offset-2' : ''
                }`}
              >
                <QuestionCard
                  question={q}
                  submission={subByQ.get(q.id)}
                  documents={docMap}
                  documentsList={documents}
                  instanceOption={instanceOptionLabel(q, questionById)}
                  lockAnswered={submittedLock && (answeredAtLoadRef.current?.has(q.id) ?? false)}
                  statusOnRight
                />
              </div>
            ))
          ) : currentStep.kind === 'review' ? (
            <ReviewSummary
              steps={steps}
              subByQ={subByQ}
              company={company}
              consentChecked={consentChecked}
              onConsentChange={setConsentChecked}
              hasTermsContent={hasTermsContent}
            />
          ) : null}
        </section>

        {currentStep.kind === 'questions' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Btn
                variant="secondary"
                disabled={safeIndex === 0}
                onClick={() => setStepIndex(Math.max(0, safeIndex - 1))}
                className="!rounded-lg !normal-case !tracking-normal !font-medium"
              >
                ← Back
              </Btn>

              {isLastStep ? (
                // Once submitted there is nothing left to submit — answers to
                // newly added questions auto-save as they are entered.
                submittedLock || submitMutation.isSuccess ? null : (
                  <Btn
                    onClick={handleSubmitClick}
                    loading={submitMutation.isPending}
                    className={`!rounded-lg !normal-case !tracking-normal !font-semibold ${
                      submitReady ? '' : 'opacity-60'
                    }`}
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    {submitButtonLabel}
                  </Btn>
                )
              ) : (
                <Btn
                  onClick={() => (canAdvance ? setStepIndex(safeIndex + 1) : revealFirstBlocker())}
                  className={`!rounded-lg !normal-case !tracking-normal !font-semibold ${
                    canAdvance ? '' : 'opacity-60'
                  }`}
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  {submittedLock ? 'Next →' : 'Continue →'}
                </Btn>
              )}
            </div>

            <div className="mt-3 text-center text-xs text-slate-500">
              {currentComplete.required === 0
                ? 'No required questions in this step.'
                : `${currentComplete.complete}/${currentComplete.required} required answered in this step`}
            </div>

            {blockers.length > 0 && (
              <div className="mx-auto mt-3 max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-xs font-semibold text-amber-800">
                  Finish {blockers.length} required question
                  {blockers.length === 1 ? '' : 's'} to continue:
                </div>
                <ul className="mt-2 space-y-1">
                  {blockers.map((b) => (
                    <li key={b.question.id}>
                      <button
                        type="button"
                        onClick={() => revealBlocker(b.question.id)}
                        className="text-left text-xs text-amber-900 hover:underline"
                      >
                        • {b.question.label}{' '}
                        <span className="text-amber-700">— {blockerHint(b.reason)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {extraGate && isLastStep && (
              <div className="mt-2 text-center">{extraGate.render()}</div>
            )}
            {submitMutation.error && (
              <div className="mt-3">
                <ErrorMessage error={submitMutation.error} />
              </div>
            )}
            {exitTo && (
              <div className="mt-6 text-center">
                <Link
                  to={exitTo}
                  className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
                >
                  ← Exit and save for later
                </Link>
              </div>
            )}
          </div>
        )}

        {currentStep.kind === 'review' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Btn
                variant="secondary"
                onClick={() => setStepIndex(Math.max(0, safeIndex - 1))}
                className="!rounded-lg !normal-case !tracking-normal !font-medium"
              >
                ← Back
              </Btn>
              <Btn
                onClick={handleSubmitApplication}
                loading={submitMutation.isPending}
                disabled={!contentComplete || !consentChecked}
                className={`!rounded-lg !normal-case !tracking-normal !font-semibold ${
                  contentComplete && consentChecked ? '' : 'opacity-60'
                }`}
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                Submit Application
              </Btn>
            </div>

            {!contentComplete && (
              <div className="mt-3 text-center text-xs text-amber-700">
                Some required items are still incomplete.{' '}
                <button
                  type="button"
                  onClick={() => {
                    const idx = firstIncompleteStepIndex();
                    if (idx >= 0) setStepIndex(idx);
                  }}
                  className="underline"
                >
                  Go to the first incomplete step
                </button>
              </div>
            )}

            {submitMutation.error && (
              <div className="mt-3">
                <ErrorMessage error={submitMutation.error} />
              </div>
            )}
          </div>
        )}

        {/* Task 7: submit-confirmation summary modal */}
        <Modal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Review your answers"
          size="lg"
          footer={
            <>
              <Btn
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
                className="!rounded-lg !normal-case !tracking-normal !font-medium"
              >
                Cancel
              </Btn>
              <Btn
                onClick={handleConfirmSubmit}
                loading={submitMutation.isPending}
                className="!rounded-lg !normal-case !tracking-normal !font-semibold"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                Submit for review
              </Btn>
            </>
          }
        >
          <SubmitSummary steps={steps} subByQ={subByQ} />
        </Modal>
      </div>
    </WizardContext.Provider>
  );
}

function Stepper({
  steps,
  activeIndex,
  completionFor,
  labelFor,
  lockedFrom,
  onSelect,
}: {
  steps: Step[];
  activeIndex: number;
  completionFor: (s: Step) => { required: number; complete: number };
  labelFor: (s: Step) => string;
  lockedFrom: number;
  onSelect: (i: number) => void;
}) {
  function keyFor(step: Step, i: number): string {
    if (step.kind === 'company-info') return 'company-info';
    if (step.kind === 'review') return 'review';
    return `${step.category.id}-${i}`;
  }
  return (
    <ol className="hidden w-full items-start sm:flex">
      {steps.map((step, i) => {
        const c = completionFor(step);
        const done = c.required > 0 && c.complete >= c.required;
        const isActive = i === activeIndex;
        const isReached = i <= activeIndex || done;
        const circleStyle: React.CSSProperties = isActive
          ? { backgroundColor: 'var(--brand-primary)', color: '#fff' }
          : done
            ? { backgroundColor: 'var(--brand-primary)', color: '#fff', opacity: 0.85 }
            : { backgroundColor: '#E2E8F0', color: '#64748B' };
        const connectorStyle: React.CSSProperties = isReached
          ? { backgroundColor: 'var(--brand-primary)' }
          : { backgroundColor: '#E2E8F0' };
        const label = labelFor(step);
        const locked = isStepLocked(i, lockedFrom);

        return (
          <li
            key={keyFor(step, i)}
            className="relative flex flex-1 flex-col items-center"
            style={{ minWidth: 0 }}
          >
            {/* Connector to next step — positioned to span between circle centres */}
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className="absolute top-4 h-0.5"
                style={{ left: '50%', right: '-50%', ...connectorStyle }}
              />
            )}
            <button
              type="button"
              onClick={() => (locked ? undefined : onSelect(i))}
              disabled={locked}
              aria-disabled={locked || undefined}
              className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition focus:outline-none ${
                locked ? 'cursor-not-allowed opacity-50' : 'hover:opacity-90'
              }`}
              style={circleStyle}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Step ${i + 1}: ${label}`}
            >
              {done && !isActive ? '✓' : i + 1}
            </button>
            <span
              className={`mt-2 px-1 text-center text-xs ${
                isActive ? 'font-semibold' : 'font-medium'
              }`}
              style={{
                color: isActive ? 'var(--brand-ink)' : '#64748B',
                wordBreak: 'break-word',
              }}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ----------------------------------------------------------------------
// Task 12: Post-submit read-only category view with pending badges
// ----------------------------------------------------------------------

function PostSubmitCategoryView({
  company,
  questions,
  submissions,
  categories,
  documents,
  visibleIds,
  completionByQuestion,
  subByQ,
  docMap,
  questionById,
  applicableQuestionnaires,
  onClearType,
  apiPrefix,
  meQueryKey,
  submissionsQueryKey,
  defaultActionTab,
}: {
  company: Company | undefined;
  questions: Question[];
  submissions: Submission[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
  subByQ: Map<string, Submission>;
  docMap: Map<string, CompanyDocument>;
  questionById: Map<string, Question>;
  applicableQuestionnaires: Questionnaire[];
  onClearType: () => void;
  apiPrefix: string;
  meQueryKey: readonly unknown[];
  submissionsQueryKey: readonly unknown[];
  defaultActionTab?: boolean;
}) {
  const visible = useMemo(
    () => questions.filter((q) => visibleIds.has(q.id)),
    [questions, visibleIds],
  );
  const needs = useMemo(
    () =>
      visible.filter(
        (q) => needsVendorAction(q, subByQ.get(q.id), completionByQuestion) !== null,
      ),
    [visible, subByQ, completionByQuestion],
  );
  const needIds = useMemo(() => new Set(needs.map((q) => q.id)), [needs]);
  const total = visible.length;
  const needCount = needs.length;
  const complete = total - needCount;

  // Default to Needs action when deep-linked (?action=1) or there is work — computed
  // once on mount. The call site keys this component by questionnaire id, so it
  // remounts (re-applying this default) when the vendor switches questionnaires.
  const [actionTab, setActionTab] = useState<'needs' | 'all'>(() =>
    defaultActionTab || needCount > 0 ? 'needs' : 'all',
  );

  // Collapsed category ids (empty = all expanded). Collapse UI shows on the
  // "All questions" tab only; "Needs action" renders flat.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const shown = actionTab === 'needs' ? needs : visible;

  // The banner reflects the review state of THIS questionnaire (not the whole
  // vendor), and only once they've submitted (review/changes/compliant). While
  // still filling it out (in_progress/invited) there is no banner — even though
  // autosaved answers exist as `pending` submissions. Scoping to the open
  // questionnaire's submissions keeps an admin-returned item in a *different*
  // questionnaire from raising the "review the marked questions" banner here.
  const submitted =
    company?.status === 'review' ||
    company?.status === 'changes' ||
    company?.status === 'compliant';
  const scopedQuestionIds = new Set(questions.map((q) => q.id));
  const scopedSubmissions = submissions.filter((s) => scopedQuestionIds.has(s.question_id));
  const bannerTurn: ReviewTurn = submitted ? reviewTurn(scopedSubmissions) : 'in_progress';
  const bannerLabel = VENDOR_REVIEW_LABEL[bannerTurn];
  const bannerClass =
    bannerTurn === 'awaiting_vendor'
      ? 'border-orange-200 bg-orange-50 text-orange-800'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700'; // awaiting_admin + compliant => success green

  return (
    <WizardContext.Provider
      value={{
        apiPrefix,
        meQueryKey,
        submissionsQueryKey,
        setLocalAnswer: () => {},
        clearLocalAnswer: () => {},
      }}
    >
      <div className="space-y-5">
        {applicableQuestionnaires.length > 1 && (
          <button
            type="button"
            onClick={onClearType}
            className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
          >
            ← All questionnaires
          </button>
        )}

        {bannerLabel && (
          <div className={`rounded-md border px-3 py-2 text-sm ${bannerClass}`}>{bannerLabel}</div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="font-medium text-slate-700">
              <span className={needCount > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                {needCount}
              </span>{' '}
              of {total} questions need your action
            </span>
            <span className="text-xs text-slate-500">{complete} complete</span>
          </div>
          <ProgressBar value={total === 0 ? 0 : Math.round((complete / total) * 100)} />
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white">
          <Tabs
            tabs={[
              { key: 'needs', label: `Needs action`, badge: needCount, badgeClassName: 'bg-red-500 text-white' },
              { key: 'all', label: `All questions (${total})` },
            ]}
            active={actionTab}
            onSelect={(k) => setActionTab(k as 'needs' | 'all')}
            ariaLabel="Questionnaire view"
            className="px-4 pt-3"
          />
          <div role="tabpanel" className="bg-slate-50 p-4">
            {(() => {
              const groups = groupByCategoryOrdered(shown, categories);
              const collapsible = actionTab === 'all';
              const allCollapsed =
                groups.length > 0 && groups.every((g) => collapsed.has(g.id));
              if (shown.length === 0) {
                return (
                  <p className="py-6 text-center text-sm text-slate-400">
                    {actionTab === 'needs' ? 'Nothing needs your action.' : 'No questions.'}
                  </p>
                );
              }
              return (
                <>
                  {collapsible && groups.length > 0 && (
                    <div className="mb-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setCollapsed(
                            allCollapsed ? new Set() : new Set(groups.map((g) => g.id)),
                          )
                        }
                        className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
                      >
                        {allCollapsed ? 'Expand all' : 'Collapse all'}
                      </button>
                    </div>
                  )}
                  <div className="space-y-6">
                    {groups.map((group) => (
                      <CategorySection
                        key={group.id}
                        name={group.name}
                        collapsible={collapsible}
                        open={!collapsed.has(group.id)}
                        onToggle={() =>
                          setCollapsed((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.id)) next.delete(group.id);
                            else next.add(group.id);
                            return next;
                          })
                        }
                      >
                        {group.items.map((q) => (
                          <div key={q.id} id={`q-${q.id}`}>
                            <QuestionCard
                              question={q}
                              submission={subByQ.get(q.id)}
                              documents={docMap}
                              documentsList={documents}
                              instanceOption={instanceOptionLabel(q, questionById)}
                              lockAnswered={postSubmitLocked(
                                q,
                                company?.status,
                                subByQ.get(q.id),
                                completionByQuestion,
                              )}
                              postSubmit
                              statusOnRight
                              needsAction={needIds.has(q.id)}
                            />
                          </div>
                        ))}
                      </CategorySection>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </section>
      </div>
    </WizardContext.Provider>
  );
}

// Category block for the post-submit view. Collapsible on the "All questions"
// tab (chevron toggles its questions); flat (always open, no chevron) on
// "Needs action" so outstanding items are never hidden.
function CategorySection({
  name,
  collapsible,
  open,
  onToggle,
  children,
}: {
  name: string;
  collapsible: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  if (!collapsible) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-900">{name}</h3>
        <div className="space-y-3">{children}</div>
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="mb-2 flex w-full items-center gap-2 text-left"
      >
        <svg
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
            clipRule="evenodd"
          />
        </svg>
        <h3 className="text-sm font-bold text-slate-900">{name}</h3>
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------
// Task 7: Submit-confirmation summary modal content
// ----------------------------------------------------------------------

function SubmitSummary({ steps, subByQ }: { steps: Step[]; subByQ: Map<string, Submission> }) {
  const questionSteps = steps.filter(
    (s): s is Extract<Step, { kind: 'questions' }> => s.kind === 'questions',
  );

  if (questionSteps.length === 0) {
    return <p className="text-sm text-slate-500">No questions to review.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Please review your answers before submitting. Once submitted, your questionnaire will be
        sent to the admin for review.
      </p>
      {questionSteps.map((step) => {
        const total = step.questions.length;
        const answered = step.questions.filter((q) => {
          const sub = subByQ.get(q.id);
          return (
            sub &&
            (sub.answer !== null ||
              sub.submission_files.length > 0 ||
              sub.submission_adoptions.length > 0)
          );
        }).length;

        return (
          <div
            key={step.category.id}
            className="rounded-lg border border-slate-200 bg-slate-50 p-4"
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">{step.category.name}</div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  answered === total
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {answered}/{total} answered
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// Vendor review step: read-only summary of vendor info + all answers,
// plus the accuracy/consent checkbox.
// ----------------------------------------------------------------------

function ReviewSummary({
  steps,
  subByQ,
  company,
  consentChecked,
  onConsentChange,
  hasTermsContent,
}: {
  steps: Step[];
  subByQ: Map<string, Submission>;
  company: Company | undefined;
  consentChecked: boolean;
  onConsentChange: (checked: boolean) => void;
  hasTermsContent: boolean;
}) {
  const { settings } = usePortalSettings();
  const [openDoc, setOpenDoc] = useState<'terms' | 'privacy' | null>(null);
  const questionSteps = steps.filter(
    (s): s is Extract<Step, { kind: 'questions' }> => s.kind === 'questions',
  );
  // Only surface the vendor-information summary while the company-info step is
  // actually part of this wizard. companyInfoStepVisible drops that step once
  // the vendor submitted their info with the first questionnaire (and re-adds
  // it only if a required field later goes missing), so this summary follows
  // suit — later questionnaires don't re-show already-submitted company details.
  const showCompanyInfo = steps.some((s) => s.kind === 'company-info');
  const infoValues = company ? toCompanyInfoFormValues(company) : null;

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Please review everything below before submitting. Use "Back" to make changes.
      </p>

      {infoValues && showCompanyInfo && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900">Vendor information</h3>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {COMPANY_INFO_FORM_FIELDS.map((field) => {
              const value = infoValues[field.key];
              const display = value != null && String(value).trim() ? String(value) : '—';
              return (
                <div key={field.key} className="flex flex-col">
                  <dt className="text-xs font-medium text-slate-500">
                    {field.labelKey
                      ? companyInfoLabel(settings, field.labelKey)
                      : field.label}
                  </dt>
                  <dd className="text-sm text-slate-900">{display}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      {questionSteps.map((step) => (
        <div key={step.category.id} className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900">{step.category.name}</h3>
          <div className="mt-3 space-y-4">
            {step.questions.map((q) => (
              <div
                key={q.id}
                className="border-t border-slate-100 pt-3 first:border-t-0 first:pt-0"
              >
                <div className="text-sm font-medium text-slate-700">{q.label}</div>
                <div className="mt-1">
                  <AnswerPartsView parts={summarizeAnswer(q, subByQ.get(q.id))} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}


      {hasTermsContent ? (
        // Single consent row combining the accuracy confirmation with the
        // Terms/Privacy agreement. Rendered as a <div> (not a <label>) with an
        // aria-label on the checkbox so clicking the inline link-buttons opens
        // the popup without toggling the box.
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="mt-0.5 h-4 w-4"
            aria-label="I confirm all information provided is accurate and complete, and I agree to the Terms and Conditions and Privacy Policy"
            required
          />
          <span className="text-sm text-slate-700">
            I confirm all information provided is accurate and complete, and I
            agree to the{' '}
            {settings?.termsContent ? (
              <button
                type="button"
                onClick={() => setOpenDoc('terms')}
                className="font-medium text-[#4D148C] underline hover:text-[#3B0F6B]"
              >
                Terms &amp; Conditions
              </button>
            ) : (
              'Terms & Conditions'
            )}{' '}
            and{' '}
            {settings?.privacyContent ? (
              <button
                type="button"
                onClick={() => setOpenDoc('privacy')}
                className="font-medium text-[#4D148C] underline hover:text-[#3B0F6B]"
              >
                Privacy Policy
              </button>
            ) : (
              'Privacy Policy'
            )}
            .
          </span>
        </div>
      ) : (
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span className="text-sm text-slate-700">
            I confirm all information provided is accurate and complete.
          </span>
        </label>
      )}

      {openDoc && (
        <Modal
          open
          title={openDoc === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'}
          size="lg"
          onClose={() => setOpenDoc(null)}
          footer={
            <Btn variant="secondary" size="sm" onClick={() => setOpenDoc(null)}>
              Close
            </Btn>
          }
        >
          <Markdown>
            {(openDoc === 'terms' ? settings?.termsContent : settings?.privacyContent) ?? ''}
          </Markdown>
        </Modal>
      )}
    </div>
  );
}

function AnswerPartsView({ parts }: { parts: AnswerPart[] }) {
  if (parts.length === 0) {
    return <span className="text-sm italic text-slate-400">Not answered</span>;
  }
  return (
    <div className="space-y-2">
      {parts.map((part, i) => {
        if (part.kind === 'text') {
          return (
            <div key={i} className="whitespace-pre-wrap text-sm text-slate-900">
              {part.value}
            </div>
          );
        }
        if (part.kind === 'list') {
          return (
            <ul key={i} className="list-inside list-disc text-sm text-slate-900">
              {part.values.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          );
        }
        if (part.kind === 'files') {
          return (
            <ul key={i} className="space-y-1 text-sm text-slate-700">
              {part.names.map((n, j) => (
                <li key={`${n}-${j}`}>📎 {n}</li>
              ))}
            </ul>
          );
        }
        if (part.kind === 'adoptions') {
          return (
            <ul key={i} className="space-y-1 text-sm text-slate-700">
              {part.items.map((it, j) => (
                <li key={j}>
                  {it.mode === 'adopted' ? '✓ Adopted standard policy' : '📎 Uploaded own document'}
                  {it.option ? ` (${it.option})` : ''}
                </li>
              ))}
            </ul>
          );
        }
        // signature
        return (
          <div key={i} className="text-sm text-slate-700">
            <img
              src={part.dataUrl}
              alt="Signature"
              className="h-20 w-auto rounded border border-slate-200 bg-white"
            />
            <div className="mt-1">
              Signed by <span className="font-medium">{part.signerName}</span> on{' '}
              {new Date(part.signedAt).toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// Company information step
// ----------------------------------------------------------------------

function CompanyInfoStep({
  company,
  isLastStep,
  onSaved,
  meQueryKey,
  apiPrefix,
}: {
  company: Company;
  isLastStep: boolean;
  onSaved: () => void;
  meQueryKey: readonly unknown[];
  apiPrefix: string;
}) {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();
  const [form, setForm] = useState<CompanyInfoFormValues>(() => toCompanyInfoFormValues(company));
  const [error, setError] = useState<unknown>(null);

  // Re-seed the local form whenever the saved company record changes (e.g.
  // returning to this step after edits sync from another tab).
  useEffect(() => {
    setForm(toCompanyInfoFormValues(company));
  }, [company.id, company.updated_at]);

  const locked = company.status === 'review' || company.status === 'compliant';
  // ABN + legal entity name stay locked once the vendor has submitted at least
  // once — including after a resend reverts them to in_progress — while the rest
  // of the step becomes editable again.
  const identityLocked = locked || !!company.has_submitted_before;
  const isIdentityField = (key: string) => key === 'abn' || key === 'legal_entity_name';

  function set<K extends keyof CompanyInfoFormValues>(key: K, value: CompanyInfoFormValues[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const saveMutation = useMutation({
    // buildCompanyInfoPayload throws on an invalid employee_count; the throw
    // propagates as a rejected mutation and surfaces via onError below.
    mutationFn: () =>
      companyApi.patch<{ company: Company }>(apiPrefix, buildCompanyInfoPayload(form)),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: meQueryKey });
      onSaved();
    },
    onError: (err) => setError(err),
  });

  const abnCheck = useAbnVerification(identityLocked ? '' : form.abn, 'company');

  // Auto-fill registered details from a verified ABN. Smart-sync: fills when
  // blank or still holding our own prior fill; never clobbers a manual edit.
  const abnFill = abnCheck.state === 'verified' ? abnCheck.result : null;
  useAutoFillField(abnFill?.entityName ?? null, form.legal_entity_name, (v) =>
    set('legal_entity_name', v),
  );
  useAutoFillField(abnFill?.addressState ?? null, form.state, (v) => set('state', v));
  useAutoFillField(abnFill?.addressPostcode ?? null, form.postcode, (v) => set('postcode', v));

  // Required fields still empty — derived from the shared submit gate
  // (REQUIRED_COMPANY_INFO_FIELDS) so this step validates exactly the set the
  // gate enforces. See companyInfoForm.ts / companyInfoForm.test.ts.
  const missing = companyInfoFormMissing(form);
  // When locked (submitted for review) the fields are read-only — let the
  // user advance through the wizard without re-saving. Otherwise gate on
  // all required fields being present.
  const canContinue = locked || missing.length === 0;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Complete your business details before answering the questionnaire. Required fields must be
        completed to continue.
      </p>

      {locked && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Submitted — fields are locked for review.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {COMPANY_INFO_FORM_FIELDS.map((field) => (
          <Field
            key={field.key}
            label={
              field.labelKey ? companyInfoLabel(settings, field.labelKey) : field.label
            }
            hint={field.hint}
            required={field.required}
          >
            <TextInput
              {...(field.inputType ? { type: field.inputType } : {})}
              {...(field.inputType === 'number' ? { min: 0 } : {})}
              value={form[field.key]}
              disabled={isIdentityField(field.key) ? identityLocked : locked}
              onChange={(e) => set(field.key, e.target.value)}
            />
            {field.key === 'abn' && (
              <AbnStatusIndicator state={abnCheck.state} result={abnCheck.result} />
            )}
          </Field>
        ))}
      </div>

      <ErrorMessage error={error} />

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        <span className="mr-auto text-xs text-slate-500">
          {missing.length === 0
            ? 'All required fields completed'
            : `${missing.length} required field${missing.length === 1 ? '' : 's'} remaining`}
        </span>
        <Btn
          onClick={() => (locked ? onSaved() : saveMutation.mutate())}
          loading={saveMutation.isPending}
          disabled={!canContinue}
          className="!rounded-lg !normal-case !tracking-normal !font-semibold"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {locked ? 'Continue →' : isLastStep ? 'Save & submit for review →' : 'Save & continue →'}
        </Btn>
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  submission,
  documents,
  documentsList,
  instanceOption,
  lockAnswered,
  postSubmit = false,
  statusOnRight = false,
  needsAction = false,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
  instanceOption: string | null;
  // True once the questionnaire is submitted and this question already had an
  // answer on file — the inputs render read-only like an approved submission.
  lockAnswered: boolean;
  // True in the post-submit grouped view, where document replacements go
  // through the comment composer — uploaded documents render as the read-only
  // version-history table even while the question is editable (sent back).
  postSubmit?: boolean;
  // Render an always-on status pill on the right of the header (incl. "Not
  // answered", so an unanswered question still shows a status and the title
  // never reflows when one appears). Used by both the wizard and post-submit.
  statusOnRight?: boolean;
  // True when this question currently needs vendor action (rejected, admin
  // comment awaiting reply, or unanswered-required) — the card border turns
  // red so the item stands out on the "All questions" tab too.
  needsAction?: boolean;
}) {
  const { apiPrefix, submissionsQueryKey, meQueryKey } = useWizardCtx();
  const qc = useQueryClient();

  const submissionId = submission?.id;
  const commentsKey = [...submissionsQueryKey, submissionId, 'comments'] as const;

  const commentsQuery = useQuery({
    queryKey: commentsKey,
    queryFn: () =>
      companyApi.get<{ comments: SubmissionComment[] }>(
        `${apiPrefix}/submissions/${submissionId}/comments`,
      ),
    // Only fetch a thread for a real server row. Synthetic submissions (an answer
    // typed but not yet saved) have a non-UUID `local-` id the API rejects with
    // "Invalid id parameter" — there is no thread to fetch for an unsaved answer.
    enabled: isPersistedSubmission(submission),
    retry: 1,
  });

  const filesHistoryKey = [...submissionsQueryKey, submissionId, 'files-history'] as const;
  const filesHistoryQuery = useQuery({
    queryKey: filesHistoryKey,
    queryFn: () =>
      companyApi.get<{ files: DocVersionFile[] }>(
        `${apiPrefix}/submissions/${submissionId}/files/history`,
      ),
    // Same gate as commentsQuery — no history to fetch for an unsaved answer.
    enabled: isPersistedSubmission(submission),
    retry: 1,
  });

  const postCommentMutation = useMutation({
    mutationFn: async (payload: ReplyPayload) => {
      const attachments = [];
      for (const a of payload.attachments) {
        const presigned = await companyApi.post<{ path: string; url: string; token: string }>(
          `${apiPrefix}/submissions/${question.id}/upload-url`,
          { file_name: a.file.name, mime_type: a.file.type || 'application/octet-stream' },
        );
        await uploadFileToSignedUrl(presigned.url, a.file);
        attachments.push({
          attachment_path: presigned.path,
          attachment_name: a.file.name,
          attachment_size_bytes: a.file.size,
          attachment_mime_type: a.file.type || 'application/octet-stream',
          attachment_expires_at: a.expiresAt ?? undefined,
          replaces_file_ids: a.replacesFileIds,
          mode: a.mode,
        });
      }
      return companyApi.post(`${apiPrefix}/submissions/${submissionId}/comments`, {
        ...(payload.body ? { body: payload.body } : {}),
        ...(attachments.length ? { attachments } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: filesHistoryKey });
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
    },
  });

  const resubmitMutation = useMutation({
    mutationFn: () => companyApi.post(`${apiPrefix}/submissions/${question.id}/resubmit`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
      void qc.invalidateQueries({ queryKey: commentsKey });
    },
  });

  const completion = questionCompletion(question, submission);
  const adoptionTargets = activeAdoptionTargets(question, submission);
  const locked =
    lockAnswered || submission?.status === 'approved' || submission?.status === 'not_applicable';

  const hasAdminComment = (commentsQuery.data?.comments ?? []).some(
    (cm) => cm.author_type === 'admin',
  );

  const historyFiles = filesHistoryQuery.data?.files ?? [];
  const hasDocuments = historyFiles.some((f) => f.superseded_at === null);

  // Vendor can reply when the question is rejected (admin requested changes),
  // when the thread already has an admin comment on a pending question (admin
  // opened the conversation without formally rejecting), or when an approved
  // document's live file has expired — the renewal door: the reply carries the
  // replacement and the API demotes the answer for admin re-verification.
  const canReply =
    !!submissionId &&
    (submission?.status === 'rejected' ||
      (submission?.status === 'pending' && hasAdminComment) ||
      (submission?.status === 'approved' && hasExpiredLiveFile(historyFiles, new Date())));

  if (question.type === 'informational') {
    return <InformationalCard question={question} submission={submission} locked={locked} />;
  }

  if (question.type === 'image_info') {
    return <ImageInfoCard question={question} />;
  }

  return (
    <div
      className={`rounded-xl border bg-white p-5 ${needsAction ? 'border-red-400' : 'border-slate-200'}`}
    >
      <div className="mb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm font-medium text-slate-900">
            {question.label}
            {instanceOption && (
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                For: {instanceOption}
              </span>
            )}
            {question.required && <span className="ml-1 text-red-500">*</span>}
          </div>
          {statusOnRight && (
            <div className="shrink-0">
              <QuestionStatusBadge status={questionDisplayStatus(submission)} />
            </div>
          )}
        </div>
        {question.help_text && (
          <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
        )}
      </div>

      {submission?.status === 'not_applicable' && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Marked not applicable by reviewer — no action needed.
        </div>
      )}

      {question.type === 'multiselect_uploads' && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Tick each option that applies, then upload a document for each ticked option before you
          can continue.
        </div>
      )}

      <AnswerInput question={question} submission={submission} locked={locked} />

      {question.type === 'multiselect_uploads' ? (
        <MultiselectUploadsEditor
          question={question}
          submission={submission}
          documents={documents}
          disabled={locked}
        />
      ) : (
        <>
          {(question.requires_upload ||
            question.upload_label ||
            (question.type === 'document_upload' && !question.adoptable_doc_id)) &&
            (showDocumentTable({
              questionType: question.type,
              hasDocuments,
              locked,
              postSubmit,
            }) ? (
              <div className="mt-3">
                <DocumentTable files={historyFiles} />
              </div>
            ) : (
              <FilesEditor question={question} submission={submission} disabled={locked} />
            ))}

          {adoptionTargets.length > 0 && (
            <AdoptionsEditor
              question={question}
              submission={submission}
              targets={adoptionTargets}
              documents={documents}
              documentsList={documentsList}
              disabled={locked}
            />
          )}
        </>
      )}

      {/* 'no_answer' and 'upload_required' are already conveyed on the card —
          by the right-side status badge and the visible dropzone (plus the
          step-footer blocker list) — so only reasons without another cue
          (adoption, video, expiry) get a hint here. */}
      {completion && completion !== 'no_answer' && completion !== 'upload_required' && (
        <div className="mt-3 text-xs text-slate-500">{completionMessage(completion)}</div>
      )}

      {submission?.status === 'rejected' && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3">
          <Btn
            size="sm"
            onClick={() => resubmitMutation.mutate()}
            loading={resubmitMutation.isPending}
            disabled={!!completion}
          >
            Submit to admin
          </Btn>
          <span className="text-xs text-slate-500">
            {completion
              ? completionMessage(completion)
              : 'Send your updated answer back to the admin for review.'}
          </span>
          {resubmitMutation.isError && <ErrorMessage error={resubmitMutation.error} />}
        </div>
      )}

      {/* Comment thread — shown below the vendor's answer when there are
          messages or the question is actionable (rejected/pending). canReply is
          true for rejected (vendor must respond) and pending (admin may have
          left a standalone comment). */}
      {submissionId && commentsQuery.isError ? (
        <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
          <ErrorMessage error={commentsQuery.error} />
          <button
            type="button"
            className="text-xs text-[#4D148C] underline hover:text-[#3B0F6B]"
            onClick={() => void commentsQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : submissionId && (commentsQuery.data?.comments.length || canReply) ? (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <CommentThread comments={commentsQuery.data?.comments ?? []} />
          {canReply && (
            <div className="mt-3">
              <ReplyComposer
                role="vendor"
                isDocumentQuestion={question.type === 'document_upload'}
                allowMultipleFiles={question.allow_multiple_files}
                currentFiles={historyFiles}
                onReply={(p) => postCommentMutation.mutate(p)}
                pending={postCommentMutation.isPending}
                error={postCommentMutation.error as Error | null}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function completionMessage(
  reason: 'no_answer' | 'upload_required' | 'adoption_required' | 'video_unwatched' | 'expiry_required',
): string {
  switch (reason) {
    case 'no_answer':
      return '⏳ Awaiting your answer';
    case 'upload_required':
      return '⏳ A file upload is required';
    case 'adoption_required':
      return '⏳ Adopt or upload the linked policy';
    case 'video_unwatched':
      return '⏳ Watch the video and mark it as watched';
    case 'expiry_required':
      return '⏳ Set an expiry date on the uploaded document';
  }
}

// ----------------------------------------------------------------------
// Informational card (read-only content, optional video)
// ----------------------------------------------------------------------

function InformationalCard({
  question,
  submission,
  locked,
}: {
  question: Question;
  submission: Submission | undefined;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey, setLocalAnswer, clearLocalAnswer } =
    useWizardCtx();
  const answer = submission?.answer as { watched_at?: unknown } | null | undefined;
  const watchedAt =
    answer &&
    typeof answer === 'object' &&
    typeof (answer as { watched_at?: unknown }).watched_at === 'string'
      ? (answer as { watched_at: string }).watched_at
      : null;

  const markWatchedMutation = useMutation({
    mutationFn: (watchedAtIso: string) =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}`, {
        answer: { watched_at: watchedAtIso },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: () => {
      // Save failed — undo the optimistic "watched" mark so the step gate
      // reflects that the video still needs confirming.
      clearLocalAnswer(question.id);
    },
  });

  function markWatched() {
    const iso = new Date().toISOString();
    // Reflect "watched" in the gate immediately so Continue isn't blocked while
    // the save is in flight.
    setLocalAnswer(question.id, { watched_at: iso }, null);
    markWatchedMutation.mutate(iso);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-base font-semibold text-slate-900">{question.label}</div>
      {question.help_text && (
        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {question.help_text}
        </div>
      )}

      {question.video_url && (
        <div className="mt-4">
          <VideoEmbed url={question.video_url} />
        </div>
      )}

      {question.video_url && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {watchedAt ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              ✓ Marked as watched on {new Date(watchedAt).toLocaleString()}
            </span>
          ) : (
            <Btn
              size="sm"
              onClick={markWatched}
              loading={markWatchedMutation.isPending}
              disabled={locked}
            >
              I have watched the video
            </Btn>
          )}
          {question.required && !watchedAt && (
            <span className="text-xs text-slate-500">
              You must watch the video and confirm before continuing to the next step.
            </span>
          )}
          {markWatchedMutation.error && <ErrorMessage error={markWatchedMutation.error} />}
        </div>
      )}
    </div>
  );
}

function ImageInfoCard({ question }: { question: Question }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-base font-semibold text-slate-900">{question.label}</div>
      {question.image_url && (
        <div className="mt-3">
          <img
            src={question.image_url}
            alt={question.label}
            className="w-full rounded-lg border border-slate-200 object-contain"
          />
        </div>
      )}
      {question.help_text && (
        <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {question.help_text}
        </div>
      )}
    </div>
  );
}

function VideoEmbed({ url }: { url: string }) {
  const lower = url.toLowerCase();
  const isNativeVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg');

  if (isNativeVideo) {
    return (
      <video controls className="w-full rounded-lg border border-slate-200 bg-black">
        <source src={url} />
      </video>
    );
  }

  return (
    <div className="aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-black">
      <iframe
        src={url}
        title="Embedded video"
        className="h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Answer input
// ----------------------------------------------------------------------

function AnswerInput({
  question,
  submission,
  locked,
}: {
  question: Question;
  submission: Submission | undefined;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey, setLocalAnswer, clearLocalAnswer } =
    useWizardCtx();
  const [local, setLocal] = useState<Json | null | undefined>(submission?.answer);
  const [touched, setTouched] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (input: { answer: Json | null; comment: string | null }) =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
      setTouched(false);
    },
    onError: () => {
      // The save failed, so the answer was NOT persisted. Drop the optimistic
      // overlay so the step gate reflects reality (the question reverts to
      // outstanding) and the error below tells the company to retry.
      clearLocalAnswer(question.id);
    },
  });

  // Report what the user entered to the wizard immediately, then persist it.
  // The gate reads the reported value at once, so a freshly-answered question
  // is never listed as "still required" while the PUT is in flight.
  function report(answer: Json | null) {
    setLocalAnswer(question.id, answer, null);
  }
  function commit(answer: Json | null) {
    report(answer);
    saveMutation.mutate({ answer, comment: null });
  }

  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    return null; // handled by AdoptionsEditor / FilesEditor below
  }

  const saveError = saveMutation.error ? (
    <div className="mt-2">
      <ErrorMessage error={saveMutation.error} />
    </div>
  ) : null;

  let body: React.ReactNode;

  if (question.type === 'signature') {
    body = (
      <SignatureInput
        submission={submission}
        locked={locked}
        saving={saveMutation.isPending}
        onSave={(value) => {
          setLocal(value as unknown as Json);
          commit(value as unknown as Json);
        }}
        onClear={() => {
          setLocal(null);
          commit(null);
        }}
      />
    );
  } else if (question.type === 'yesno') {
    const value = (local ?? submission?.answer) as boolean | null | undefined;
    body = (
      <div className="flex gap-2">
        {[
          { v: true, label: 'Yes' },
          { v: false, label: 'No' },
        ].map((o) => (
          <button
            key={String(o.v)}
            disabled={locked}
            onClick={() => {
              setLocal(o.v);
              commit(o.v);
            }}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              value === o.v
                ? 'border-transparent text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            } ${locked ? 'opacity-60' : ''}`}
            style={value === o.v ? { backgroundColor: 'var(--brand-accent)' } : {}}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else if (question.type === 'select') {
    const value = (local ?? submission?.answer) as string | null | undefined;
    body = (
      <Select
        disabled={locked}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value || null;
          setLocal(v);
          commit(v);
        }}
      >
        <option value="">— Select —</option>
        {(question.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </Select>
    );
  } else if (question.type === 'multiselect' || question.type === 'multiselect_uploads') {
    const value = ((local ?? submission?.answer) as string[] | null | undefined) ?? [];
    body = (
      <div className="grid gap-2 sm:grid-cols-2">
        {(question.options ?? []).map((o) => {
          const checked = value.includes(o);
          return (
            <label
              key={o}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                checked ? 'border-current' : 'border-slate-200'
              } ${locked ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}
              style={checked ? { color: 'var(--brand-accent)' } : {}}
            >
              <input
                type="checkbox"
                disabled={locked}
                checked={checked}
                onChange={() => {
                  const next = checked ? value.filter((v) => v !== o) : [...value, o];
                  setLocal(next);
                  commit(next);
                }}
              />
              <span className="text-slate-700">{o}</span>
            </label>
          );
        })}
      </div>
    );
  } else {
    // text
    const value = (local ?? submission?.answer ?? '') as string;
    body = (
      <div>
        <TextArea
          disabled={locked}
          value={String(value)}
          onChange={(e) => {
            setLocal(e.target.value);
            setTouched(true);
            // Reflect typing in the gate right away so a single "Continue"
            // click advances; the value is persisted on blur below.
            report(e.target.value);
          }}
          onBlur={() => {
            if (touched) commit(value);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {body}
      {saveError}
    </>
  );
}

// ----------------------------------------------------------------------
// Signature
// ----------------------------------------------------------------------

type SignatureAnswerValue = {
  signature_data_url: string;
  signer_name: string;
  signed_at: string;
};

function isSignatureAnswer(value: unknown): value is SignatureAnswerValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.signature_data_url === 'string' &&
    typeof v.signer_name === 'string' &&
    typeof v.signed_at === 'string'
  );
}

function SignatureInput({
  submission,
  locked,
  saving,
  onSave,
  onClear,
}: {
  submission: Submission | undefined;
  locked: boolean;
  saving: boolean;
  onSave: (value: SignatureAnswerValue) => void;
  onClear: () => void;
}) {
  const existing = isSignatureAnswer(submission?.answer) ? submission!.answer : null;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasStrokeRef = useRef(false);
  const [signerName, setSignerName] = useState(existing?.signer_name ?? '');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function startStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (locked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointerPos(e);
  }

  function drawStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPointRef.current;
    if (!canvas || !ctx || !last) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasStrokeRef.current = true;
    if (!hasDrawn) setHasDrawn(true);
  }

  function endStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasStrokeRef.current = false;
    setHasDrawn(false);
  }

  function handleSave() {
    setError(null);
    if (!hasStrokeRef.current) {
      setError('Please draw your signature.');
      return;
    }
    const name = signerName.trim();
    if (!name) {
      setError('Please type your name.');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave({
      signature_data_url: dataUrl,
      signer_name: name,
      signed_at: new Date().toISOString(),
    });
  }

  function handleClear() {
    setError(null);
    clearCanvas();
    setSignerName('');
    onClear();
  }

  if (existing) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <img
            src={existing.signature_data_url}
            alt="Signature"
            className="h-32 w-full object-contain"
          />
        </div>
        <div className="text-sm text-slate-700">
          <div>
            Signed by <span className="font-medium">{existing.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">
            {new Date(existing.signed_at).toLocaleString()}
          </div>
        </div>
        {!locked && (
          <Btn variant="ghost" size="sm" onClick={handleClear} loading={saving}>
            Clear &amp; re-sign
          </Btn>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 text-xs font-medium text-slate-600">Draw your signature below</div>
        <canvas
          ref={canvasRef}
          onPointerDown={startStroke}
          onPointerMove={drawStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          className="h-40 w-full touch-none rounded-lg border border-dashed border-slate-300 bg-white"
          style={{ cursor: locked ? 'not-allowed' : 'crosshair' }}
        />
      </div>
      <Field label="Full name" required>
        <input
          type="text"
          value={signerName}
          disabled={locked}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="Type your full name"
          className="block w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm placeholder:text-slate-400"
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Btn size="sm" onClick={handleSave} loading={saving} disabled={locked}>
          Save signature
        </Btn>
        <Btn
          size="sm"
          variant="ghost"
          onClick={() => {
            setError(null);
            clearCanvas();
          }}
          disabled={locked || !hasDrawn}
        >
          Clear drawing
        </Btn>
        <span className="text-xs text-slate-400">
          Signed-at timestamp is recorded automatically.
        </span>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------
// Files
// ----------------------------------------------------------------------

function UploadedFileRow({
  questionId,
  file,
  disabled,
  requiresExpiry,
}: {
  questionId: string;
  file: SubmissionFile;
  disabled: boolean;
  requiresExpiry: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const state = expiryState(file);

  async function setExpiry(value: string) {
    const iso = fromDateInput(value);
    setError(null);
    setSaving(true);
    try {
      await companyApi.patch(`${apiPrefix}/submissions/${questionId}/files/${file.id}`, {
        expires_at: iso,
      });
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function downloadFile() {
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/submissions/${questionId}/files/${file.id}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function deleteFile() {
    setError(null);
    try {
      await companyApi.delete(`${apiPrefix}/submissions/${questionId}/files/${file.id}`);
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  const showExpiryRow = !disabled || !!file.expires_at;
  const expiryRequiredMissing = requiresExpiry && !file.expires_at;

  return (
    <li className="px-3.5 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#F3EBFA] text-[#4D148C]">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 3v5h5" />
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            </svg>
          </span>
          <div className="min-w-0">
            <button
              onClick={() => void downloadFile()}
              className="block max-w-full truncate text-sm font-semibold text-[#4D148C] hover:underline"
            >
              {file.file_name}
            </button>
            <div className="text-[11.5px] text-slate-400">
              {fileKindLabel(file.file_name, file.mime_type)} · {formatFileSize(file.size_bytes)}
            </div>
          </div>
        </div>
        {!disabled && (
          <button
            onClick={() => void deleteFile()}
            aria-label={`Remove ${file.file_name}`}
            className="flex-shrink-0 text-xs font-semibold text-red-600 hover:underline"
          >
            Remove
          </button>
        )}
      </div>

      {showExpiryRow && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2.5 pl-12">
          <span className={`text-xs font-semibold ${expiryRequiredMissing ? 'text-red-600' : 'text-slate-600'}`}>
            Expires
          </span>
          <DateField
            value={toDateInput(file.expires_at)}
            onChange={(v) => void setExpiry(v)}
            disabled={disabled || saving}
            ariaLabel="Expiry date"
            placeholder="dd mmm yyyy"
            invalid={expiryRequiredMissing}
          />
          <ValidityBadge state={state} requiresExpiry={requiresExpiry} hasDate={!!file.expires_at} />
        </div>
      )}
      <ErrorMessage error={error} />
    </li>
  );
}

function ValidityBadge({
  state,
  requiresExpiry,
  hasDate,
}: {
  state: ExpiryState;
  requiresExpiry: boolean;
  hasDate: boolean;
}) {
  const base = 'inline-flex items-center gap-1 text-xs font-semibold';
  if (state === 'ok') {
    return (
      <span className={`${base} text-emerald-700`}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 12.5l2.5 2.5 4.5-5" />
        </svg>
        Valid
      </span>
    );
  }
  if (state === 'expiring') {
    return (
      <span className={`${base} text-amber-700`}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5v5l3 2" />
        </svg>
        Expiring soon
      </span>
    );
  }
  if (state === 'expired') {
    return (
      <span className={`${base} text-red-700`}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3.5 21 19.5H3z" />
          <path d="M12 10v4M12 17h.01" />
        </svg>
        Expired
      </span>
    );
  }
  // state === 'none'
  if (requiresExpiry && !hasDate) {
    return <span className={`${base} text-red-600`}>Required</span>;
  }
  return null;
}

function ExpiryErrorBanner({ count }: { count: number }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
      <svg width="18" height="18" className="flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3.5 21 19.5H3z" />
        <path d="M12 10v4M12 17h.01" />
      </svg>
      <span className="text-[13.5px] font-semibold text-red-700">
        {count} file{count === 1 ? '' : 's'} still need{count === 1 ? 's' : ''} an expiry date before you can
        submit.
      </span>
    </div>
  );
}

// The redesigned upload area shared by FilesEditor (one question) and
// OptionUploadRow (one multiselect option). Empty -> the full dropzone; with
// files -> a missing-expiry banner (when required), the bordered file list, and
// the compact "add another" dropzone. Files upload as soon as they are picked;
// expiry is filled in afterwards on the uploaded row (the submit gate keeps a
// required expiry from being skipped). Presentation only; the caller owns the
// upload handler and `files` source (whole-question vs option-scoped).
function UploadFileArea({
  questionId,
  files,
  requiresExpiry,
  allowMultiple,
  disabled,
  uploading,
  onUpload,
}: {
  questionId: string;
  files: SubmissionFile[];
  requiresExpiry: boolean;
  allowMultiple: boolean;
  disabled: boolean;
  uploading: boolean;
  onUpload: (items: File[]) => Promise<void>;
}) {
  function pick(list: FileList) {
    const picked = allowMultiple ? Array.from(list) : Array.from(list).slice(0, 1);
    if (picked.length > 0) void onUpload(picked);
  }

  if (files.length === 0) {
    return (
      <FileDrop
        variant="full"
        onFiles={pick}
        accept={DOCUMENT_ACCEPT}
        multiple={allowMultiple}
        disabled={disabled || uploading}
        hint={uploading ? 'Uploading…' : undefined}
      />
    );
  }
  const missingExpiry = requiresExpiry ? files.filter((f) => !f.expires_at).length : 0;
  return (
    <>
      {missingExpiry > 0 && <ExpiryErrorBanner count={missingExpiry} />}
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
        {files.map((f) => (
          <UploadedFileRow
            key={f.id}
            questionId={questionId}
            file={f}
            disabled={disabled}
            requiresExpiry={requiresExpiry}
          />
        ))}
      </ul>
      {!disabled && allowMultiple && (
        <div className="mt-3">
          <FileDrop
            variant="compact"
            onFiles={pick}
            accept={DOCUMENT_ACCEPT}
            disabled={uploading}
            hint={uploading ? 'Uploading…' : undefined}
          />
        </div>
      )}
    </>
  );
}

function FilesEditor({
  question,
  submission,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = submission?.submission_files ?? [];

  async function uploadFiles(items: File[]) {
    setError(null);
    setUploading(true);
    try {
      const chosen = question.allow_multiple_files ? items : items.slice(0, 1);
      for (const file of chosen) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `${apiPrefix}/submissions/${question.id}/upload-url`,
          { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`${apiPrefix}/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
        });
      }
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
      await qc.invalidateQueries({ queryKey: meQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mt-3">
      {question.upload_label && (
        <div className="mb-2 text-xs text-slate-500">{question.upload_label}</div>
      )}
      <UploadFileArea
        questionId={question.id}
        files={files}
        requiresExpiry={question.requires_expiry}
        allowMultiple={question.allow_multiple_files}
        disabled={disabled}
        uploading={uploading}
        onUpload={uploadFiles}
      />
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Per-option uploads (multiselect_uploads)
// ----------------------------------------------------------------------

function MultiselectUploadsEditor({
  question,
  submission,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const selected = (submission?.answer as string[] | null | undefined) ?? [];
  if (selected.length === 0) return null;

  const triggerByOption = new Map<string, { documentId: string; adoptOnly: boolean }>();
  for (const t of question.adoption_triggers) {
    // multiselect_uploads maps each option to a doc via trigger_value; an
    // unconditional (null) trigger has no option key and does not belong here.
    if (t.trigger_value == null) continue;
    triggerByOption.set(t.trigger_value, {
      documentId: t.document_id,
      adoptOnly: t.adopt_only,
    });
  }

  return (
    <div className="mt-4 space-y-3">
      {selected.map((opt) => (
        <OptionUploadRow
          key={opt}
          question={question}
          submission={submission}
          option={opt}
          trigger={triggerByOption.get(opt) ?? null}
          documents={documents}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function OptionUploadRow({
  question,
  submission,
  option,
  trigger,
  documents,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  option: string;
  trigger: { documentId: string; adoptOnly: boolean } | null;
  documents: Map<string, CompanyDocument>;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const files = (submission?.submission_files ?? []).filter((f) => f.option_key === option);
  const adoption = (submission?.submission_adoptions ?? []).find(
    (a) => a.option_key === option && trigger && a.document_id === trigger.documentId,
  );

  const adoptableDoc = trigger ? documents.get(trigger.documentId) : undefined;

  async function uploadFiles(items: File[]) {
    setError(null);
    setUploading(true);
    try {
      for (const file of items) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `${apiPrefix}/submissions/${question.id}/upload-url`,
          {
            file_name: file.name,
            mime_type: file.type || 'application/octet-stream',
            option_key: option,
          },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        await companyApi.post(`${apiPrefix}/submissions/${question.id}/files`, {
          file_path: presigned.path,
          file_name: file.name,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          option_key: option,
        });
      }
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
      await qc.invalidateQueries({ queryKey: meQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  async function adopt() {
    if (!trigger) return;
    setError(null);
    try {
      await companyApi.put(
        `${apiPrefix}/submissions/${question.id}/adoptions/${trigger.documentId}`,
        { mode: 'adopted', option_key: option },
      );
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  async function clearAdoption() {
    if (!trigger) return;
    try {
      await companyApi.delete(
        `${apiPrefix}/submissions/${question.id}/adoptions/${trigger.documentId}`,
      );
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err);
    }
  }

  async function downloadStandard() {
    if (!trigger) return;
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/documents/${trigger.documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  const adopted = adoption?.mode === 'adopted';
  const satisfied = adopted || files.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-slate-900">{option}</div>
          {satisfied ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              ✓ Provided
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              Document required
            </span>
          )}
        </div>
        {trigger && adoptableDoc && (
          <button
            onClick={() => void downloadStandard()}
            className="text-xs text-[#4D148C] hover:underline"
          >
            View standard: {adoptableDoc.name}
          </button>
        )}
      </div>

      {trigger && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Btn
            size="sm"
            variant={adopted ? 'primary' : 'secondary'}
            disabled={disabled}
            onClick={() => void adopt()}
          >
            {adopted ? '✓ Adopted' : 'Adopt this policy'}
          </Btn>
          {adopted && !disabled && (
            <button
              onClick={() => void clearAdoption()}
              className="text-xs text-red-600 hover:underline"
            >
              Clear adoption
            </button>
          )}
          {!trigger.adoptOnly && <span className="text-xs text-slate-400">or upload below</span>}
        </div>
      )}

      {(!trigger || !trigger.adoptOnly) && (
        <div className="mt-3">
          <UploadFileArea
            questionId={question.id}
            files={files}
            requiresExpiry={question.requires_expiry}
            allowMultiple={true}
            disabled={disabled}
            uploading={uploading}
            onUpload={uploadFiles}
          />
        </div>
      )}
      <ErrorMessage error={error} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Adoptions (document_adopt + adoption triggers)
// ----------------------------------------------------------------------

function AdoptionsEditor({
  question,
  submission,
  targets,
  documents,
  documentsList,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  targets: { documentId: string; adoptOnly: boolean }[];
  documents: Map<string, CompanyDocument>;
  documentsList: CompanyDocument[];
  disabled: boolean;
}) {
  return (
    <div className="mt-4 space-y-3">
      {targets.map((t) => (
        <DocumentAdoptionRow
          key={t.documentId}
          question={question}
          submission={submission}
          documentId={t.documentId}
          adoptOnly={t.adoptOnly}
          document={documents.get(t.documentId)}
          allDocs={documentsList}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function DocumentAdoptionRow({
  question,
  submission,
  documentId,
  adoptOnly,
  document,
  allDocs,
  disabled,
}: {
  question: Question;
  submission: Submission | undefined;
  documentId: string;
  adoptOnly: boolean;
  document: CompanyDocument | undefined;
  allDocs: CompanyDocument[];
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const { apiPrefix, meQueryKey, submissionsQueryKey } = useWizardCtx();
  const [error, setError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);

  const adoption = submission?.submission_adoptions.find((a) => a.document_id === documentId);

  const adoptMutation = useMutation({
    mutationFn: () =>
      companyApi.put(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'adopted',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsQueryKey });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      companyApi.delete(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: submissionsQueryKey }),
  });

  async function downloadDoc() {
    const res = await companyApi
      .get<{ url: string }>(`${apiPrefix}/documents/${documentId}/download-url`)
      .catch(() => null);
    if (res?.url) window.open(res.url, '_blank', 'noopener');
  }

  async function uploadOwn(files: FileList) {
    setError(null);
    setUploading(true);
    try {
      const file = files.item(0);
      if (!file) return;
      const presigned = await companyApi.post<{ path: string; url: string }>(
        `${apiPrefix}/submissions/${question.id}/upload-url`,
        { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await companyApi.put(`${apiPrefix}/submissions/${question.id}/adoptions/${documentId}`, {
        mode: 'uploaded',
        file_path: presigned.path,
      });
      await qc.invalidateQueries({ queryKey: submissionsQueryKey });
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setUploading(false);
    }
  }

  const docName = document?.name ?? allDocs.find((d) => d.id === documentId)?.name ?? 'Document';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">{docName}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {adoptOnly
              ? 'You must adopt this standard policy.'
              : 'Adopt the standard policy or upload your own equivalent.'}
          </div>
        </div>
        <button
          onClick={() => void downloadDoc()}
          className="text-xs text-[#4D148C] hover:underline"
        >
          View standard
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn
          size="sm"
          variant={adoption?.mode === 'adopted' ? 'primary' : 'secondary'}
          disabled={disabled}
          loading={adoptMutation.isPending}
          onClick={() => adoptMutation.mutate()}
        >
          {adoption?.mode === 'adopted' ? '✓ Adopted' : 'Adopt this policy'}
        </Btn>
        {!adoptOnly && (
          <>
            <span className="text-xs text-slate-400">or</span>
            <label
              className={`text-xs ${disabled ? 'opacity-50' : 'cursor-pointer text-[#4D148C] hover:underline'}`}
            >
              <input
                type="file"
                className="hidden"
                disabled={disabled || uploading}
                onChange={(e) => {
                  if (e.target.files?.length) uploadOwn(e.target.files);
                  e.target.value = '';
                }}
              />
              {uploading
                ? 'Uploading…'
                : adoption?.mode === 'uploaded'
                  ? 'Replace upload'
                  : 'Upload your own'}
            </label>
            {adoption?.mode === 'uploaded' && adoption.file_path && (
              <span className="ml-2 text-xs text-slate-500">
                {adoption.file_path.split('/').pop()}
              </span>
            )}
          </>
        )}
        {adoption && !disabled && (
          <button
            onClick={() => deleteMutation.mutate()}
            className="ml-auto text-xs text-red-600 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <ErrorMessage error={error} />
    </div>
  );
}
